import React from 'react';
import { Container } from 'components/elements';
import {
  PageShell,
  PageHeader,
  PageTitle,
  EffectiveDate,
  Content,
  Intro,
  TableOfContents,
  Callout,
  ContactBlock,
} from './legal.styled';

const sections = [
  { id: 'commitment', title: 'Our Commitment to Your Safety' },
  { id: 'information-we-collect', title: 'Information We Collect' },
  { id: 'how-we-use', title: 'How We Use Information' },
  { id: 'google-user-data', title: 'Google User Data — Limited Use' },
  { id: 'membership-tiers', title: 'Membership Tiers and What Others Can See' },
  { id: 'how-we-share', title: 'How We Share Information' },
  { id: 'retention', title: 'Data Retention and Deletion' },
  { id: 'children', title: "Children's Privacy" },
  { id: 'security', title: 'Data Security' },
  { id: 'your-rights', title: 'Your Choices and Rights' },
  { id: 'high-risk', title: 'A Note for Members in High-Risk Regions' },
  { id: 'cookies', title: 'Cookies' },
  { id: 'third-party-links', title: 'Third-Party Links' },
  { id: 'changes', title: 'Changes to This Policy' },
  { id: 'contact', title: 'Contact Us' },
];

export default function PrivacyPolicy() {
  return (
    <PageShell>
      <Container>
        <PageHeader>
          <PageTitle>Privacy Policy</PageTitle>
          <EffectiveDate>Effective Date: August 28, 2026</EffectiveDate>
        </PageHeader>

        <Content>
          <Intro>
            Zoroastrian Polgozar operates the website
            polgozar.org (the &quot;Service&quot;), an online Zoroastrian community, calendar-sharing platform, and
            ministry. Polgozar is organized as a California nonprofit religious corporation. This Privacy Policy
            explains what information we collect, why, how we protect it, and the choices you have — including special
            protections we&apos;ve built in because we know many members of the worldwide Zoroastrian community,
            particularly Zoroastrians connected to Iran, have real and serious safety concerns about being identified by
            their faith.
          </Intro>
          <p>If you do not agree with this Policy, please do not use the Service.</p>

          <TableOfContents aria-label="Table of contents">
            <h2>Contents</h2>
            <ol>
              {sections.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`}>{section.title}</a>
                </li>
              ))}
            </ol>
          </TableOfContents>

          <h2 id="commitment">1. Our Commitment to Your Safety</h2>
          <p>
            Zoroastrians are a small, geographically scattered faith community, and for many — especially those with
            family or ties in Iran — this community&apos;s safety depends on two things that can seem to pull in
            opposite directions: knowing that the person you&apos;re talking to is who they say they are (so bad actors
            can&apos;t infiltrate the community under a false identity), and making sure that identity is never exposed
            outside the trusted circle of Full Members. Polgozar is built around both:
          </p>
          <Callout>
            <p>
              <strong>
                Applicants for Full Membership are confirmed through a reference check by fellow Zoroastrians, Full
                Members use their real, verified identity within the community, and that identity information is never
                public, never visible to Guests, and never disclosed outside the Service except as described in Section
                6.
              </strong>
            </p>
          </Callout>
          <p>This principle governs every design and data decision described below.</p>

          <h2 id="information-we-collect">2. Information We Collect</h2>

          <h3>a. Information You Provide</h3>
          <ul>
            <li>
              <strong>Account information:</strong> an email address and your real, full name. We ask for your real name
              (rather than a pseudonym) because our community has told us that knowing who they&apos;re actually
              speaking with is essential to trust and safety in a small, targeted community — see Section 5 for exactly
              who can see this.
            </li>
            <li>
              <strong>Reference information:</strong> to apply for Full Membership, you provide the email addresses of
              two people who can personally confirm you are Zoroastrian (&quot;references&quot;). References do not need
              to already be Polgozar members. We contact each reference by email to request their confirmation. Approval
              currently requires an affirmative response from <strong>at least one</strong> of the two references; we
              collect and retain the second reference now so that we have the option to require confirmation from both
              references in the future as the community grows, and we will update this Policy if that requirement
              changes.
            </li>
            <li>
              <strong>Optional profile information:</strong> anything you choose to add beyond your name (bio, location
              region, interests). All optional fields are marked as such, and none require proof of identity or faith
              beyond the attestation and sponsorship described below.
            </li>
            <li>
              <strong>Self-attested faith affiliation:</strong> to join as a Full Member, you affirm that you identify
              as Zoroastrian. This is a good-faith attestation, reinforced by the sponsorship requirement above — we do
              not request or store religious documentation, conversion certificates, or any third-party religious
              verification.
            </li>
            <li>
              <strong>Content you submit:</strong> event listings, RSVPs, messages to other members, and any curriculum
              or discussion content you post.
            </li>
          </ul>

          <h3>b. Information About Your References (Third Parties)</h3>
          <p>
            Because the reference-check process above involves contacting people who may not themselves be applying to
            join Polgozar, we want to be transparent about this separately: we use a reference&apos;s email address to
            send them a request to confirm your Zoroastrian identity, and we record their yes/no response (and, if
            given, brief comments). We do not add a reference to any mailing list, create an account for them, or use
            their email for any other purpose without their own consent.
          </p>
          <p>
            If your application is denied, or a reference never responds, we delete that reference&apos;s contact
            information within a reasonable period. However,{' '}
            <strong>
              if you are admitted as a Full Member, we retain the link between your account and your references for as
              long as you remain a member (and for a limited period after, if needed to resolve an open safety issue).
            </strong>{' '}
            We do this deliberately: if a member is later found to pose a safety risk, misrepresents who they are, or
            otherwise abuses the trust of the community, we may go back to the reference(s) who vouched for that member
            to inform them and ask them to account for their attestation. A reference&apos;s information and response
            are visible only to Polgozar administrators — never to other members, Guests, or the public — and are used
            only for admission decisions and this kind of follow-up accountability, never for marketing or any unrelated
            purpose.
          </p>

          <h3>c. Information from Connected Google Calendars</h3>
          <p>
            When a group (such as ZYNA) chooses to connect its Google Calendar to Polgozar, our integration requests{' '}
            <strong>read-only</strong> access to that specific calendar. We use this access only to:
          </p>
          <ul>
            <li>
              Display event details (title, date/time, location, and description) that the connecting organization has
              chosen to make available through their calendar.
            </li>
          </ul>
          <p>
            We do <strong>not</strong> request or access your personal Gmail, Google Contacts, files, or any other
            Google account data, and the integration cannot create, edit, or delete anything in a connected Google
            Calendar.
          </p>

          <h3>d. Automatically Collected Information</h3>
          <p>
            We collect minimal technical data needed to operate and secure the Service: browser type, device type, and
            access timestamps. <strong>We intentionally minimize IP address logging</strong> — where logs are kept for
            security purposes (e.g., abuse prevention), we truncate or aggregate IP addresses and delete detailed logs
            on a short retention cycle rather than tying them permanently to your account.
          </p>

          <h3>e. Information from Minors</h3>
          <p>
            See Section 8 (Children&apos;s Privacy) below for what we collect from members under 13 and how parental
            consent works.
          </p>

          <h2 id="how-we-use">3. How We Use Information</h2>
          <p>We use the information above only to:</p>
          <ul>
            <li>Create and maintain your account and membership tier;</li>
            <li>Display community events and enable RSVPs;</li>
            <li>Operate the Google Calendar import feature described above;</li>
            <li>Send you Service-related communications (e.g., event reminders you opt into);</li>
            <li>Maintain the safety, security, and integrity of the community; and</li>
            <li>
              Improve the Service&apos;s features, including the Ashavan Path curriculum and future educational
              materials.
            </li>
          </ul>
          <p>
            We do <strong>not</strong> use your information for advertising, and we do <strong>not</strong> build
            advertising profiles.
          </p>

          <h2 id="google-user-data">4. Google User Data — Limited Use Disclosure</h2>
          <p>
            Polgozar&apos;s use and transfer of information received from Google APIs adheres to the{' '}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements. Specifically:
          </p>
          <ul>
            <li>
              Calendar data is used <strong>only</strong> to provide and improve the event-display feature described
              above and is never used for advertising or sold to third parties.
            </li>
            <li>
              Calendar data is never transferred to any other party except as necessary to provide the feature, to
              comply with applicable law, or as part of a legitimate business transfer disclosed to you in advance.
            </li>
            <li>
              No human reviews connected Calendar data except: (1) with your affirmative consent, (2) for security
              purposes (e.g., investigating abuse), (3) to comply with applicable law, or (4) where the data has been
              aggregated and de-identified.
            </li>
          </ul>

          <h2 id="membership-tiers">5. Membership Tiers and What Others Can See</h2>
          <p>Polgozar deliberately limits visibility based on membership tier:</p>
          <ul>
            <li>
              <strong>Full Members</strong> (self-attested, sponsored Zoroastrians) can see the community event
              calendar, message other Full Members, and participate in community features. A Full Member&apos;s real
              name is visible to <strong>other logged-in Full Members only</strong> — this is the core trust mechanism
              that lets our community verify who they&apos;re talking to and screen out impostors.
            </li>
            <li>
              <strong>Guests/Invitees</strong> (non-Zoroastrians invited to a specific event by a Full Member) can only
              view and respond to <strong>the single event they were invited to</strong>. Guests cannot browse the
              member directory, cannot see any other events, cannot see any Full Member&apos;s real name beyond what the
              inviting member chooses to share for that event, and cannot message any member on the Service — including
              the person who invited them.
            </li>
          </ul>
          <p>
            We do not publish a public membership directory, no member profile or real name is ever visible to the
            public internet or to search engines, and no information is visible to Guests beyond what&apos;s described
            above.
          </p>

          <h2 id="how-we-share">6. How We Share Information</h2>
          <p>
            We do not sell your personal information, and we do not share it with data brokers or advertisers. We share
            information only with:
          </p>
          <ul>
            <li>
              <strong>Service providers</strong> who help us run the Service (e.g., hosting), bound by confidentiality
              obligations and prohibited from using your data for their own purposes;
            </li>
            <li>
              <strong>Other members</strong>, only to the extent your membership tier and privacy settings allow (see
              Section 5); and
            </li>
            <li>
              <strong>Legal authorities</strong>, only as described immediately below.
            </li>
          </ul>

          <h3>Government and Legal Requests</h3>
          <p>
            Given the safety concerns many of our members face, we take requests for member information extremely
            seriously. Our policy is to:
          </p>
          <ol>
            <li>
              Scrutinize every request for legal validity and scope, and challenge or narrow any request that is
              overbroad, vague, or not properly issued by a court of competent jurisdiction;
            </li>
            <li>Provide only the minimum information legally required, if any;</li>
            <li>
              Notify the affected member before disclosure whenever legally permitted, so they have the opportunity to
              seek their own legal counsel or object; and
            </li>
            <li>
              Never voluntarily disclose a member&apos;s religious affiliation, identity, or activity to any government,
              foreign or domestic, absent a legally binding order we have unsuccessfully contested.
            </li>
          </ol>

          <h2 id="retention">7. Data Retention and Deletion</h2>
          <p>
            We retain your information only as long as your account is active or as needed to provide the Service. You
            may delete your account at any time from your account settings, or by emailing us at{' '}
            <a href="mailto:admin@polgozar.org">admin@polgozar.org</a>. Upon deletion, we remove your personal
            information from active systems within 30 days and from backups within 90 days, except where limited
            retention is required for security, legal compliance, or dispute resolution.
          </p>
          <p>
            As described in Section 2(b), the identity of your references and their attestation are retained for the
            duration of your active membership specifically so that we can follow up with a reference if a safety or
            trust concern arises later — this is a deliberate exception to the general rule above, not an oversight.
            When your account is deleted or your membership is revoked, we retain the reference record only for a
            limited period reasonably necessary to close out any open safety review, and then delete it consistent with
            the schedule above.
          </p>

          <h2 id="children">8. Children&apos;s Privacy (Parental Consent Program)</h2>
          <p>
            Because ZYNA (Zoroastrian Youth of North America) and similar youth programs are part of our community, we
            allow members under 13 to join <strong>only</strong> through our parental consent process:
          </p>
          <ul>
            <li>
              A parent or legal guardian must create or approve the child&apos;s account and provide verifiable consent
              before any personal information is collected from a child under 13, consistent with the Children&apos;s
              Online Privacy Protection Act (COPPA).
            </li>
            <li>
              We collect only the minimum information necessary for the child&apos;s participation (display
              name/pseudonym and, where required, a parent&apos;s contact email).
            </li>
            <li>
              We do not show behavioral advertising to any member, and this applies with particular force to minors.
            </li>
            <li>
              A parent or guardian may review, correct, or request deletion of their child&apos;s information at any
              time by contacting <a href="mailto:admin@polgozar.org">admin@polgozar.org</a>, and may revoke consent and
              terminate the child&apos;s account at any time.
            </li>
          </ul>

          <h2 id="security">9. Data Security</h2>
          <p>
            We use industry-standard safeguards, including encryption in transit, access controls limiting staff access
            to personal data on a need-to-know basis, and regular review of our security practices. No system is
            perfectly secure, and we describe additional precautions for high-risk members in Section 11 below.
          </p>

          <h2 id="your-rights">10. Your Choices and Rights</h2>
          <p>Regardless of where you live, you may:</p>
          <ul>
            <li>Access, correct, or export your account information;</li>
            <li>Delete your account and associated data (Section 7);</li>
            <li>Opt out of any non-essential email communications; and</li>
            <li>
              Contact us with privacy questions or requests at{' '}
              <a href="mailto:admin@polgozar.org">admin@polgozar.org</a>.
            </li>
          </ul>
          <p>
            California residents have additional rights under the California Consumer Privacy Act (CCPA), including the
            right to know what personal information we collect, the right to deletion, and the right to
            non-discrimination for exercising these rights. We do not sell personal information, so there is no
            &quot;opt-out of sale&quot; needed.
          </p>

          <h2 id="high-risk">11. A Note for Members in High-Risk Regions</h2>
          <p>
            We&apos;ve built Polgozar to minimize what we collect outside the trusted circle and who can see it, but no
            online service — including ours — can guarantee absolute security against a determined, well-resourced
            government actor. We know requiring a real name (Section 2, Section 5) is a deliberate trade-off: the
            community has told us that knowing who they&apos;re speaking to is essential to keeping impostors out, and
            we&apos;ve weighed that against exposure risk by keeping real names strictly internal to verified Full
            Members and out of any public or Guest-facing view. If you are in Iran or another environment where being
            identified as Zoroastrian could put you at risk, we still recommend:
          </p>
          <ul>
            <li>Sharing only the minimum optional profile information beyond what&apos;s required for membership;</li>
            <li>Considering a privacy-protective browser or VPN when accessing the Service; and</li>
            <li>
              Reporting any account you suspect is impersonating a Zoroastrian or was admitted without a legitimate
              sponsor, so we can investigate.
            </li>
          </ul>
          <p>
            We will continue to invest in privacy-protective design, and we welcome feedback from community members on
            additional safeguards we should consider.
          </p>

          <h2 id="cookies">12. Cookies</h2>
          <p>
            We use only essential cookies required for login sessions and basic site functionality. We do not use
            third-party advertising or tracking cookies.
          </p>

          <h2 id="third-party-links">13. Third-Party Links</h2>
          <p>
            The Service may link to third-party sites (including a connected group&apos;s own Google Calendar or
            website). We are not responsible for the privacy practices of third-party sites, and we encourage you to
            review their policies separately.
          </p>

          <h2 id="changes">14. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will post the updated version on this page with a
            new effective date, and for material changes, we will provide additional notice (such as an email or
            in-Service banner).
          </p>

          <h2 id="contact">15. Contact Us</h2>
          <p>Questions, requests, or concerns about this Privacy Policy or your data can be sent to:</p>
          <ContactBlock>
            <strong>Polgozar</strong>
            Email: <a href="mailto:admin@polgozar.org">admin@polgozar.org</a>
            <br />
            6123 Oakbridge Dr., Granite Bay, CA 95746
          </ContactBlock>
        </Content>
      </Container>
    </PageShell>
  );
}
